import { FC } from 'react';
import {
  Avatar,
  AvatarVariants,
  Button,
  PhoneIcon
} from 'stubben-edge-unified-frontend-react-components';
import { getPersonInitials } from '@/utils/audienceCardMetrics';
import { incomingGroupLabel } from '@/utils/incomingCallPresentation';
import './IncomingCallNotification.css';

const hangUpIconUrl = new URL(
  '../../assets/icons/hang-up.svg',
  import.meta.url
).href;

export const INCOMING_ACCEPT_SLIDE_MS = 320;

type Props = {
  phoneNumber: string;
  callerName?: string | null;
  callerAvatarUrl?: string | null;
  callGroupName?: string | null;
  status?: string;
  isExiting?: boolean;
  onAccept: () => void;
  onReject: () => void;
  onCallerNameClick?: () => void;
};

const IncomingCallNotification: FC<Props> = ({
  phoneNumber,
  callerName,
  callerAvatarUrl,
  callGroupName,
  status = 'Incoming call...',
  isExiting = false,
  onAccept,
  onReject,
  onCallerNameClick
}) => {
  const hasMatchedContact = Boolean(callerName || callerAvatarUrl);
  const groupLabel = incomingGroupLabel(callGroupName);

  return (
    <div
      className={`flex w-[336px] flex-col items-end justify-center gap-6 rounded-lg bg-[#363c4d] p-6 shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.1),0px_2px_4px_-2px_rgba(16,24,40,0.06)]${
        isExiting ? ' incoming-call-snackbar--exit' : ''
      }`}
      data-testid="incoming-call-notification"
    >
      <div className="flex w-full items-start gap-3">
        {hasMatchedContact ? (
          <Avatar
            size="md"
            variant={
              callerAvatarUrl ? AvatarVariants.Image : AvatarVariants.Letter
            }
            image={callerAvatarUrl || undefined}
            label={getPersonInitials(callerName)}
            showTooltip={false}
            testIdentifier="incoming-call-avatar"
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
          <p className="w-full text-lg font-medium leading-7 text-white">
            {phoneNumber}
          </p>
          {callerName ? (
            onCallerNameClick ? (
              <button
                type="button"
                className="w-full text-left text-base font-normal leading-6 text-white underline-offset-2 hover:underline"
                onClick={onCallerNameClick}
                data-testid="incoming-call-caller-name"
              >
                {callerName}
              </button>
            ) : (
              <p
                className="w-full text-base font-normal leading-6 text-white"
                data-testid="incoming-call-caller-name"
              >
                {callerName}
              </p>
            )
          ) : null}
          <p className="w-full text-sm font-normal leading-5 text-[#ddebff]">
            {status}
          </p>
          <p
            className="w-full text-sm font-normal leading-5 text-[#ddebff]"
            data-testid="incoming-call-group"
          >
            {groupLabel}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Button
          variant="primary"
          mode="error"
          leftSlot={
            <span className="inline-flex size-5 overflow-hidden">
              <img
                src={hangUpIconUrl}
                alt=""
                className="size-5"
                data-testid="incoming-call-reject-icon"
              />
            </span>
          }
          onClick={onReject}
          testIdentifier="incoming-call-reject"
        >
          Reject
        </Button>
        <Button
          variant="primary"
          mode="success"
          className="incoming-call-accept-pulse"
          leftSlot={
            <span className="incoming-call-phone-ring">
              <PhoneIcon />
            </span>
          }
          onClick={onAccept}
          testIdentifier="incoming-call-accept"
        >
          Accept
        </Button>
      </div>
    </div>
  );
};

export default IncomingCallNotification;
